import { supabase } from '../supabase';
import { currentDivisionId } from '../stores/useFiltersStore';
import {
  ClientRankingsExporters,
  Settlement,
  SettlementDetails,
  SettlementStatus,
} from '../utils/types';
import axios from 'axios';
import { sendEmail } from './email';
import { Session } from '@supabase/supabase-js';

interface CreateSettlementParams {
  exporter_id: number;
  exporter_receiver_id?: number | null;
  reference_number: string;
  receiver: string;
  legacy_receiver?: string | null;
  business_type?: string | null;
  sale_order?: string | null;
  ship_trip_id: number;
  container_number: string;
  date_sold: string;
  region: string;
  total_revenue_local: number;
  total_tax_local: number;
  total_commission_local: number;
  total_other_costs_local: number;
  total_repacking_costs_local: number;
  total_freight_usd: number;
  exchange_rate: number | null;
}

export const getCostsVsExporters = async (
  session: Session,
  methodology: string,
  season: string,
  species_id: number,
  separation: string,
  market_ids: string,
  shipmentType: string = 'both',
  countryId: string = 'all',
  variety: string = 'all',
  weekNumber?: number,
  costFilter: string = 'all'
) => {
  const costTypes = ['commission', 'tax', 'freight', 'other_costs'];
  const results: { [key: string]: any } = {};

  await Promise.all(
    costTypes.map(async (costType) => {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-costs-vs-exporters`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            methodology,
            season,
            species_id,
            division_id: currentDivisionId(),
            separation,
            market_ids,
            costType,
            shipmentType,
            country_id: countryId,
            variety: variety,
            costFilter,
            ...(weekNumber && { weekNumber }),
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch ${costType} data`);
      }
      results[costType] = await response.json();
    })
  );

  return results;
};

export const getAvailableCountries = async (
  _session: Session,
  season: string,
  species_id: number
) => {
  try {
    const { data: settlements, error } = await supabase
      .from('settlements')
      .select(
        `
          ship_trip:ship_trips(
            market:markets!ship_trips_market_id_fkey(country)
          )
        `
      )
      .eq('upload_status', 'approved')
      .eq('species_id', species_id)
      .eq('season', season)
      .not('ship_trip.market.country', 'is', null);

    if (error) throw error;

    // Count settlements by country
    const countryCount = new Map<string, number>();
    settlements?.forEach((settlement: any) => {
      const country = settlement.ship_trip?.market?.country;
      if (country) {
        countryCount.set(country, (countryCount.get(country) || 0) + 1);
      }
    });

    // Convert to array and sort by count (descending), then by name
    const countriesWithCount = Array.from(countryCount.entries())
      .map(([country, count]) => ({ country, count }))
      .sort((a, b) => {
        if (b.count !== a.count) {
          return b.count - a.count; // Sort by count descending
        }
        return a.country.localeCompare(b.country); // Then by name ascending
      });

    return {
      countries: countriesWithCount.map((item) => item.country),
      mostCommon:
        countriesWithCount.length > 0 ? countriesWithCount[0].country : null,
      countriesWithCount,
    };
  } catch (error) {
    console.error('Error fetching available countries:', error);
    return {
      countries: [],
      mostCommon: null,
      countriesWithCount: [],
    };
  }
};

export const getAvailableVarietiesWithCounts = async (
  _session: Session,
  season: string,
  species_id: number
) => {
  try {
    const { data: settlements, error } = await supabase
      .from('settlements')
      .select(
        `
          settlements_details(variety, kilos)
        `
      )
      .eq('upload_status', 'approved')
      .eq('species_id', species_id)
      .eq('season', season)
      .not('settlements_details.variety', 'is', null);

    if (error) throw error;

    // Count settlements by variety (considering kilos as weight)
    const varietyCount = new Map<string, { count: number; kilos: number }>();
    settlements?.forEach((settlement: any) => {
      settlement.settlements_details?.forEach((detail: any) => {
        if (detail.variety) {
          const current = varietyCount.get(detail.variety) || {
            count: 0,
            kilos: 0,
          };
          varietyCount.set(detail.variety, {
            count: current.count + 1,
            kilos: current.kilos + (detail.kilos || 0),
          });
        }
      });
    });

    // Convert to array and sort by kilos (descending), then by name
    const varietiesWithCount = Array.from(varietyCount.entries())
      .map(([variety, { count, kilos }]) => ({ variety, count, kilos }))
      .sort((a, b) => {
        if (b.kilos !== a.kilos) {
          return b.kilos - a.kilos; // Sort by kilos descending
        }
        return a.variety.localeCompare(b.variety); // Then by name ascending
      });

    return {
      varieties: varietiesWithCount.map((item) => item.variety),
      mostCommon:
        varietiesWithCount.length > 0 ? varietiesWithCount[0].variety : null,
      varietiesWithCount,
    };
  } catch (error) {
    console.error('Error fetching available varieties:', error);
    return {
      varieties: [],
      mostCommon: null,
      varietiesWithCount: [],
    };
  }
};

export async function createSettlement(
  params: CreateSettlementParams
): Promise<Settlement | null> {
  try {
    const { data, error } = await supabase
      .from('settlements')
      .insert([params])
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating settlement:', error);
    return null;
  }
}

export async function getSettlementsByExporterId(
  exporter_id: number | undefined,
  status?: SettlementStatus,
  season?: string,
  species_id?: number,
  sinister_filter: 'all' | 'sinister' | 'non_sinister' = 'all',
  detailsAttributes: string[] = ['*'],
  divisionId: number | null = null
): Promise<Settlement[] | null> {
  try {
    let query = supabase
      .from('settlements')
      .select(
        `
        *,
        settlement_details:settlements_details(${detailsAttributes.join(',')},settlement_detail_revenues(*)),
        ship_trip:ship_trips(
          *,
          ship:ships(*),
          market:markets!ship_trips_market_id_fkey(*),
          origin_market:markets!ship_trips_origin_market_id_fkey(*)
        ),
        market:markets!settlements_market_id_fkey(*),
        comparable_market:markets!settlements_comparable_market_id_fkey(*)
      `
      )
      .eq('exporter_id', exporter_id)
      .eq('species_id', species_id);

    if (divisionId) query = query.eq('division_id', divisionId);

    if (status) {
      query = query.eq('upload_status', status);
    }

    if (season) {
      query = query.eq('season', season);
    }

    if (sinister_filter === 'non_sinister') {
      query = query.eq('is_sinister', false);
    } else if (sinister_filter === 'sinister') {
      query = query.eq('is_sinister', true);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data as unknown as Settlement[];
  } catch (error) {
    console.error('Error getting settlements:', error);
    return null;
  }
}

/**
 * Fetches only the fields needed to populate the comparative-industry client
 * selector. Keeping this separate from getSettlementsByExporterId avoids
 * making the selector wait for every settlement detail and related record.
 */
export async function getComparativeIndustryClients(
  exporter_id: number,
  season: string,
  species_id: number,
  sinister_filter: 'all' | 'sinister' | 'non_sinister' = 'all',
  price_type: 'FOB' | 'CIF' = 'FOB',
  divisionId: number | null = null
): Promise<string[]> {
  try {
    let query = supabase
      .from('settlements')
      .select(
        `
        receiver,
        total_freight_usd,
        exchange_rate,
        ship_trip:ship_trips(ship:ships(type))
      `
      )
      .eq('exporter_id', exporter_id)
      .eq('upload_status', 'approved')
      .eq('season', season)
      .eq('species_id', species_id)
      .not('receiver', 'is', null)
      .not('exchange_rate', 'is', null);

    if (divisionId) query = query.eq('division_id', divisionId);

    if (price_type === 'FOB') {
      query = query.gt('total_freight_usd', 0);
    }

    if (sinister_filter === 'non_sinister') {
      query = query.eq('is_sinister', false);
    } else if (sinister_filter === 'sinister') {
      query = query.eq('is_sinister', true);
    }

    const { data, error } = await query;
    if (error) throw error;

    const settlements = data as unknown as Array<{
      receiver: string | null;
      ship_trip: { ship: { type: string | null } | null } | null;
    }>;
    const clients = new Set<string>();
    settlements.forEach((settlement) => {
      const shipType = settlement.ship_trip?.ship?.type;
      if ((!shipType || shipType === 'M') && settlement.receiver) {
        clients.add(settlement.receiver);
      }
    });

    return [...clients].sort((a, b) => a.localeCompare(b));
  } catch (error) {
    console.error('Error getting comparative industry clients:', error);
    return [];
  }
}

/**
 * Lightweight KPI-only RPC for fast initial render of summary cards.
 * Returns just aggregates — no rows, no nested JSON. Completes in <500ms.
 */
export interface SettlementSummaryKPIs {
  total_fob: number;
  total_kilos: number;
  settlement_count: number;
  has_missing_data: boolean;
  missing_reasons?: {
    missing_fob_or_kilos_detail_count?: number;
    settlements_without_fob_details_count?: number;
  };
}

export async function getSettlementSummaryKPIs(
  exporter_id: number,
  season: string,
  species_id: number,
  divisionId: number | null = currentDivisionId()
): Promise<SettlementSummaryKPIs | null> {
  try {
    const { data, error } = await supabase.rpc('get_settlement_summary_kpis', {
      p_exporter_id: exporter_id,
      p_season: season,
      p_species_id: species_id,
      p_division_id: divisionId,
    });

    if (error) throw error;
    return data as unknown as SettlementSummaryKPIs;
  } catch (error) {
    console.error('Error getting settlement summary KPIs:', error);
    return null;
  }
}

export async function getSettlementSummaryKPIsV2(
  exporter_id: number,
  season: string,
  species_id: number,
  divisionId: number | null = currentDivisionId()
): Promise<SettlementSummaryKPIs | null> {
  try {
    const { data, error } = await supabase.rpc(
      'get_settlement_summary_kpis_v2',
      {
        p_exporter_id: exporter_id,
        p_season: season,
        p_species_id: species_id,
        p_division_id: divisionId,
      }
    );

    if (error) throw error;
    return data as unknown as SettlementSummaryKPIs;
  } catch (error) {
    console.error('Error getting settlement summary KPIs v2:', error);
    return null;
  }
}

export interface ShipmentSettlementCounts {
  total_shipments: number;
  settled_shipments: number;
  missing_settlement_references?: string[];
  missing_settlement_references_count?: number;
}

export async function getShipmentSettlementCounts(
  exporter_id: number,
  season: string,
  species_id: number,
  divisionId: number | null = currentDivisionId()
): Promise<ShipmentSettlementCounts | null> {
  try {
    const { data, error } = await supabase.rpc(
      'get_shipment_settlement_counts',
      {
        p_exporter_id: exporter_id,
        p_season: season,
        p_species_id: species_id,
        p_division_id: divisionId,
      }
    );

    if (error) throw error;
    return data as unknown as ShipmentSettlementCounts;
  } catch (error) {
    console.error('Error getting shipment settlement counts:', error);
    return null;
  }
}

export interface DashboardFobMetrics {
  total_fob: number;
  total_kilos: number;
  settlement_count: number;
  markets: string[];
  varieties: string[];
}

export async function getDashboardFobMetrics(
  exporter_id: number,
  season: string,
  species_id: number,
  market: string = 'all',
  variety: string = 'all',
  divisionId: number | null = currentDivisionId()
): Promise<DashboardFobMetrics | null> {
  try {
    const { data, error } = await supabase.rpc('get_dashboard_fob_metrics', {
      p_exporter_id: exporter_id,
      p_season: season,
      p_species_id: species_id,
      p_division_id: divisionId,
      p_market_name: market === 'all' ? null : market,
      p_variety: variety === 'all' ? null : variety,
    });

    if (error) throw error;

    const result = (data || {}) as Partial<DashboardFobMetrics>;
    return {
      total_fob: Number(result.total_fob) || 0,
      total_kilos: Number(result.total_kilos) || 0,
      settlement_count: Number(result.settlement_count) || 0,
      markets: Array.isArray(result.markets) ? result.markets : [],
      varieties: Array.isArray(result.varieties) ? result.varieties : [],
    };
  } catch (error) {
    console.error('Error getting dashboard FOB metrics:', error);
    return null;
  }
}

export interface DashboardVstaMetricRow {
  season: string;
  week_key: string | null;
  market_name: string | null;
  variety: string | null;
  total_fob: number;
  total_kilos: number;
  settlement_count: number;
  detail_count: number;
}

export interface DashboardVstaMetrics {
  rows: DashboardVstaMetricRow[];
  varieties: string[];
}

export async function getDashboardVstaMetrics(
  exporter_id: number,
  current_season: string,
  previous_season: string,
  species_id: number,
  variety: string = 'all',
  divisionId: number | null = currentDivisionId()
): Promise<DashboardVstaMetrics | null> {
  try {
    const { data, error } = await supabase.rpc('get_dashboard_vsta_metrics', {
      p_exporter_id: exporter_id,
      p_current_season: current_season,
      p_previous_season: previous_season,
      p_species_id: species_id,
      p_division_id: divisionId,
      p_variety: variety === 'all' ? null : variety,
    });

    if (error) throw error;

    const result = (data || {}) as Partial<DashboardVstaMetrics>;
    const rows = Array.isArray(result.rows) ? result.rows : [];

    return {
      rows: rows.map((row) => ({
        ...row,
        total_fob: Number(row.total_fob) || 0,
        total_kilos: Number(row.total_kilos) || 0,
        settlement_count: Number(row.settlement_count) || 0,
        detail_count: Number(row.detail_count) || 0,
      })),
      varieties: Array.isArray(result.varieties) ? result.varieties : [],
    };
  } catch (error) {
    console.error('Error getting dashboard VSTA metrics:', error);
    return null;
  }
}

export interface DashboardFobVsExportersOwnPoint {
  value: number;
  kilos: number;
  label?: string | null;
  receiver?: string | null;
}

export interface DashboardFobVsExportersStats {
  min: number;
  q1: number;
  median: number;
  q3: number;
  max: number;
}

export interface DashboardFobVsExportersBoxPlot {
  own: DashboardFobVsExportersOwnPoint[];
  others: DashboardFobVsExportersStats;
  ownKg: number;
  othersKg: number;
}

export interface DashboardFobVsExportersWeekData {
  [caliber: string]: DashboardFobVsExportersBoxPlot;
}

export interface DashboardFobVsExportersVarietyData {
  [week: string]: DashboardFobVsExportersWeekData;
}

export interface DashboardFobVsExportersData {
  [variety: string]: DashboardFobVsExportersVarietyData;
}

export async function getDashboardFobVsExporters(
  exporter_id: number,
  season: string,
  species_id: number,
  variety: string = 'all',
  methodology: 'by_volume' | 'by_exporter' = 'by_exporter',
  incoterm: 'fob' | 'cif' = 'fob',
  date_type: 'date_sold' | 'eta' | 'etd' = 'eta',
  sinister_filter: 'all' | 'sinister' | 'non_sinister' = 'all',
  divisionId: number | null = currentDivisionId()
): Promise<DashboardFobVsExportersData | null> {
  try {
    const { data, error } = await supabase.rpc(
      'get_dashboard_fob_vs_exporters',
      {
        p_exporter_id: exporter_id,
        p_season: season,
        p_species_id: species_id,
        p_division_id: divisionId,
        p_variety: variety === 'all' ? null : variety,
        p_methodology: methodology,
        p_incoterm: incoterm,
        p_date_type: date_type,
        p_sinister_filter: sinister_filter,
      }
    );

    if (error) throw error;
    return (data || {}) as DashboardFobVsExportersData;
  } catch (error) {
    console.error('Error getting dashboard FOB vs exporters:', error);
    return null;
  }
}

export async function getSettlementsFobVsExportersRPC(
  exporter_id: number,
  methodology: 'by_volume' | 'by_exporter' = 'by_volume',
  season?: string,
  species_id?: number,
  sinister_filter: 'all' | 'sinister' | 'non_sinister' = 'all',
  separation: 'none' | 'by_label' | 'by_receiver' = 'none',
  weekNumber?: number,
  variety?: string,
  dateMethodology?: 'date_sold' | 'eta' | 'etd',
  incoterm?: 'fob' | 'cif',
  countries?: string[],
  market_ids?: string[],
  ship_trip_ids?: string[],
  label_categories?: string[],
  aggregation?: 'global',
  include_revenues: boolean = false,
  divisionId: number | null = currentDivisionId()
): Promise<DashboardFobVsExportersData | null> {
  try {
    const { data, error } = await supabase.rpc('get_fob_vs_exporters_rpc', {
      p_exporter_id: exporter_id,
      p_season: season,
      p_species_id: species_id,
      p_division_id: divisionId,
      p_methodology: methodology,
      p_sinister_filter: sinister_filter,
      p_separation: separation,
      p_week_number: weekNumber ?? null,
      p_variety: variety || null,
      p_date_type: dateMethodology || 'date_sold',
      p_incoterm: incoterm || 'fob',
      p_countries: countries && countries.length > 0 ? countries : null,
      p_market_ids: market_ids && market_ids.length > 0 ? market_ids : null,
      p_ship_trip_ids:
        ship_trip_ids && ship_trip_ids.length > 0
          ? ship_trip_ids.map((id) => Number(id))
          : null,
      p_label_categories:
        label_categories && label_categories.length > 0
          ? label_categories
          : null,
      p_aggregation: aggregation || 'detail',
      p_include_revenues: include_revenues,
    });

    if (error) throw error;
    return (data || {}) as DashboardFobVsExportersData;
  } catch (error) {
    console.error('Error getting FOB vs exporters RPC:', error);
    return null;
  }
}

export interface ExporterGlobalFobPercentile {
  own_fob: number | null;
  industry_median_fob: number | null;
  own_percentile: number | null;
}

export interface ProductionCaliberCurveRow {
  caliber: string;
  own_pct: number;
  industry_pct: number;
}

export interface ProductionWeeklyCurveRow {
  week: number;
  label: string;
  own_pct: number;
  industry_pct: number;
}

export interface ProductionVarietyCurveRow {
  variety: string;
  own_pct: number;
  industry_pct: number;
}

export interface ProductionMarketCurveRow {
  market: string;
  own_pct: number;
  industry_pct: number;
}

export interface ProductionMarketCountryCurveRow {
  country: string;
  own_pct: number;
  industry_pct: number;
}

export interface ProductionFormatUnitWeightCurveRow {
  format_unit_weight: string;
  own_pct: number;
  industry_pct: number;
}

export interface ProductionFreightTypeCurveRow {
  freight_type: string;
  own_pct: number;
  industry_pct: number;
}

export interface ProductionVsIndustryResult {
  caliber_curve: ProductionCaliberCurveRow[];
  weekly_curve: ProductionWeeklyCurveRow[];
  variety_curve: ProductionVarietyCurveRow[];
  market_curve: ProductionMarketCurveRow[];
  market_country_curve: ProductionMarketCountryCurveRow[];
  format_unit_weight_curve: ProductionFormatUnitWeightCurveRow[];
  freight_type_curve: ProductionFreightTypeCurveRow[];
  own_varieties: string[];
  has_own_data: boolean;
  has_industry_data: boolean;
  missing_dates: {
    own: number;
    industry: number;
  };
}

export async function getExporterGlobalFobPercentile(
  exporter_id: number,
  season: string,
  species_id: number,
  methodology: 'by_volume' | 'by_exporter' = 'by_exporter',
  sinister_filter: 'all' | 'sinister' | 'non_sinister' = 'all',
  divisionId: number | null = currentDivisionId()
): Promise<ExporterGlobalFobPercentile | null> {
  try {
    const { data, error } = await supabase.rpc(
      'get_exporter_global_fob_percentile',
      {
        p_exporter_id: exporter_id,
        p_season: season,
        p_species_id: species_id,
        p_division_id: divisionId,
        p_methodology: methodology,
        p_sinister_filter: sinister_filter,
      }
    );

    if (error) throw error;
    return data as unknown as ExporterGlobalFobPercentile;
  } catch (error) {
    console.error('Error getting exporter global FOB percentile:', error);
    return null;
  }
}

export async function getExporterGlobalFobPercentileV2(
  exporter_id: number,
  season: string,
  species_id: number,
  methodology: 'by_volume' | 'by_exporter' = 'by_exporter',
  sinister_filter: 'all' | 'sinister' | 'non_sinister' = 'all',
  divisionId: number | null = currentDivisionId()
): Promise<ExporterGlobalFobPercentile | null> {
  try {
    const { data, error } = await supabase.rpc(
      'get_exporter_global_fob_percentile_v2',
      {
        p_exporter_id: exporter_id,
        p_season: season,
        p_species_id: species_id,
        p_division_id: divisionId,
        p_methodology: methodology,
        p_sinister_filter: sinister_filter,
      }
    );

    if (error) throw error;
    return data as unknown as ExporterGlobalFobPercentile;
  } catch (error) {
    console.error('Error getting exporter global FOB percentile v2:', error);
    return null;
  }
}

export async function getProductionVsIndustry(
  exporter_id: number,
  season: string,
  species_id: number,
  date_type: 'etd' | 'eta' = 'etd',
  varieties: string[] = [],
  divisionId: number | null = currentDivisionId()
): Promise<ProductionVsIndustryResult | null> {
  try {
    const { data, error } = await supabase.rpc('get_production_vs_industry', {
      p_exporter_id: exporter_id,
      p_season: season,
      p_species_id: species_id,
      p_division_id: divisionId,
      p_date_type: date_type,
      p_varieties: varieties.length > 0 ? varieties : null,
    });

    if (error) throw error;
    return data as unknown as ProductionVsIndustryResult;
  } catch (error) {
    console.error('Error getting production vs industry:', error);
    return null;
  }
}

/**
 * Calls the get_settlement_summary RPC function which returns pre-filtered,
 * pre-joined settlement data with KPIs computed server-side.
 */
/**
 * RPC for the settlements details page table view.
 * SECURITY DEFINER — bypasses RLS, manual auth inside.
 * Revenues conditional via include_revenues param.
 */
export async function getSettlementDetailsPageRPC(
  exporter_id: number,
  season: string,
  species_id: number,
  include_revenues: boolean = false,
  divisionId: number | null = currentDivisionId()
): Promise<Settlement[] | null> {
  try {
    const { data, error } = await supabase.rpc('get_settlement_details_page', {
      p_exporter_id: exporter_id,
      p_season: season,
      p_species_id: species_id,
      p_division_id: divisionId,
      p_include_revenues: include_revenues,
    });

    if (error) throw error;
    return data as unknown as Settlement[];
  } catch (error) {
    console.error('Error getting settlement details page:', error);
    return null;
  }
}

export interface SourceAllocationMoneyTotal {
  currency: string;
  sales: number;
  tax: number;
  commission: number;
  freight: number;
  repacking: number;
  otherCosts: number;
  totalCosts: number;
  net: number;
}

export interface SourceReferenceAllocation extends SourceAllocationMoneyTotal {
  settlementId: number;
  referenceNumber: string;
  containerNumber: string;
  receiver: string;
  shipName: string;
  quantity: number;
  kilos: number;
}

export interface SourceSettlementGroupView {
  id: string;
  sourceDocumentId: string;
  name: string;
  pages: number[];
  extractedHeader: Record<string, unknown>;
  extractedTotals: Record<string, unknown>;
  scopeFilter: Record<string, unknown>;
  reviewStatus: string;
  quantity: number;
  kilos: number;
  settlementCount: number;
  moneyTotals: SourceAllocationMoneyTotal[];
  references: SourceReferenceAllocation[];
}

export interface SourceSettlementDocumentView {
  id: string;
  fileName: string;
  bucketId: string;
  storagePath: string;
  status: string;
  exporterReceiverId: number | null;
  receiverName: string | null;
  createdAt: string;
  appliedAt: string | null;
  groups: SourceSettlementGroupView[];
}

export async function getSettlementSourceHierarchyRPC(
  exporterId: number,
  season: string,
  speciesId: number,
  divisionId: number | null = currentDivisionId()
): Promise<SourceSettlementDocumentView[] | null> {
  try {
    const { data, error } = await supabase.rpc(
      'get_settlement_source_hierarchy',
      {
        p_exporter_id: exporterId,
        p_season: season,
        p_species_id: speciesId,
        p_division_id: divisionId,
      }
    );

    if (error) throw error;

    const documents = (data || []) as unknown as SourceSettlementDocumentView[];
    if (documents.length === 0) return documents;

    const { data: locations, error: locationsError } = await supabase
      .from('source_documents')
      .select('id, bucket_id')
      .in(
        'id',
        documents.map((document) => document.id)
      );

    if (locationsError) throw locationsError;

    const bucketByDocumentId = new Map(
      (locations || []).map((location) => [location.id, location.bucket_id])
    );

    return documents.map((document) => ({
      ...document,
      bucketId:
        bucketByDocumentId.get(document.id) ||
        'multi-shipment-source-documents',
    }));
  } catch (error) {
    console.error('Error getting settlement source hierarchy:', error);
    return null;
  }
}

// ---------------------------------------------------------------------------
// Aggregates-only RPCs for the new Settlements Summary architecture.
// Additive — does not replace any existing function.
// ---------------------------------------------------------------------------

export interface SettlementDistributionFilters {
  sinister?: 'all' | 'sinister' | 'non_sinister';
  receivers?: string[];
  markets?: string[];
  countries?: string[];
  ship_trip_ids?: number[];
  week_keys?: string[]; // format: 'IYYY-IW', e.g. '2026-15'
  cost_methodology?: string;
  date_type?: 'eta' | 'etd' | 'sale_date';
}

export interface DistributionGroupRow {
  key: string | null;
  total_fob: number;
  total_kilos: number;
  total_extra_revenue_usd: number;
  settlement_count?: number;
  detail_count?: number;
}

export interface SettlementDistributionResult {
  grand_totals: {
    settlement_count: number;
    detail_count: number;
    total_fob: number;
    total_kilos: number;
    total_extra_revenue_usd: number;
  };
  groupings: {
    client: DistributionGroupRow[];
    variety: DistributionGroupRow[];
    caliber: DistributionGroupRow[];
    format: DistributionGroupRow[];
    color: DistributionGroupRow[];
    label: DistributionGroupRow[];
    market: DistributionGroupRow[];
    country: DistributionGroupRow[];
    fleet: DistributionGroupRow[];
    business_type: DistributionGroupRow[];
  };
  filter_universe: {
    receivers: string[];
    markets: string[];
    countries: string[];
    varieties: string[];
    calibers: string[];
    colors: string[];
    formats: string[];
    labels: string[];
    fleets: string[];
    business_types: string[];
  };
}

export async function getSettlementDistribution(
  exporter_id: number,
  season: string,
  species_id: number,
  filters: SettlementDistributionFilters = {},
  divisionId: number | null = currentDivisionId()
): Promise<SettlementDistributionResult | null> {
  try {
    const { data, error } = await supabase.rpc('get_settlement_distribution', {
      p_exporter_id: exporter_id,
      p_season: season,
      p_species_id: species_id,
      p_division_id: divisionId,
      p_sinister: filters.sinister ?? 'all',
      p_receivers: filters.receivers ?? null,
      p_markets: filters.markets ?? null,
      p_countries: filters.countries ?? null,
      p_ship_trip_ids: filters.ship_trip_ids ?? null,
      p_week_keys: filters.week_keys ?? null,
      p_cost_methodology: filters.cost_methodology ?? 'all',
      p_date_type: filters.date_type ?? 'eta',
    });

    if (error) throw error;
    return data as unknown as SettlementDistributionResult;
  } catch (error) {
    console.error('Error getting settlement distribution:', error);
    return null;
  }
}

export async function getSettlementDistributionV2(
  exporter_id: number,
  season: string,
  species_id: number,
  filters: SettlementDistributionFilters = {},
  divisionId: number | null = currentDivisionId()
): Promise<SettlementDistributionResult | null> {
  try {
    const { data, error } = await supabase.rpc(
      'get_settlement_distribution_v2',
      {
        p_exporter_id: exporter_id,
        p_season: season,
        p_species_id: species_id,
        p_division_id: divisionId,
        p_sinister: filters.sinister ?? 'all',
        p_receivers: filters.receivers ?? null,
        p_markets: filters.markets ?? null,
        p_countries: filters.countries ?? null,
        p_ship_trip_ids: filters.ship_trip_ids ?? null,
        p_week_keys: filters.week_keys ?? null,
        p_cost_methodology: filters.cost_methodology ?? 'all',
        p_date_type: filters.date_type ?? 'eta',
      }
    );

    if (error) throw error;
    return data as unknown as SettlementDistributionResult;
  } catch (error) {
    console.error('Error getting settlement distribution v2:', error);
    return null;
  }
}

/**
 * Lightweight companion: only the 'client' grouping + grand totals + filter
 * universe. Meant to be called in parallel with `getSettlementDistribution`
 * for faster first paint of the Settlements Summary's Acumulado section.
 * Returns the same shape as the full RPC — the 9 non-client groupings come
 * back as empty arrays.
 */
export async function getSettlementDistributionClient(
  exporter_id: number,
  season: string,
  species_id: number,
  filters: SettlementDistributionFilters = {}
): Promise<SettlementDistributionResult | null> {
  try {
    const { data, error } = await supabase.rpc(
      'get_settlement_distribution_client',
      {
        p_exporter_id: exporter_id,
        p_season: season,
        p_species_id: species_id,
        p_sinister: filters.sinister ?? 'all',
        p_receivers: filters.receivers ?? null,
        p_markets: filters.markets ?? null,
        p_countries: filters.countries ?? null,
        p_ship_trip_ids: filters.ship_trip_ids ?? null,
        p_week_keys: filters.week_keys ?? null,
        p_cost_methodology: filters.cost_methodology ?? 'all',
        p_date_type: filters.date_type ?? 'eta',
      }
    );

    if (error) throw error;
    return data as unknown as SettlementDistributionResult;
  } catch (error) {
    console.error('Error getting settlement distribution (client):', error);
    return null;
  }
}

export async function getSettlementDistributionClientV2(
  exporter_id: number,
  season: string,
  species_id: number,
  filters: SettlementDistributionFilters = {}
): Promise<SettlementDistributionResult | null> {
  try {
    const { data, error } = await supabase.rpc(
      'get_settlement_distribution_client_v2',
      {
        p_exporter_id: exporter_id,
        p_season: season,
        p_species_id: species_id,
        p_sinister: filters.sinister ?? 'all',
        p_receivers: filters.receivers ?? null,
        p_markets: filters.markets ?? null,
        p_countries: filters.countries ?? null,
        p_ship_trip_ids: filters.ship_trip_ids ?? null,
        p_week_keys: filters.week_keys ?? null,
        p_cost_methodology: filters.cost_methodology ?? 'all',
        p_date_type: filters.date_type ?? 'eta',
      }
    );

    if (error) throw error;
    return data as unknown as SettlementDistributionResult;
  } catch (error) {
    console.error('Error getting settlement distribution client v2:', error);
    return null;
  }
}

export interface SettlementSummaryResult {
  kpis: {
    total_fob: number;
    total_kilos: number;
    avg_fob_per_kg: number;
    settlement_count: number;
    has_missing_data: boolean;
    missing_reasons?: {
      missing_fob_or_kilos_detail_count?: number;
      settlements_without_fob_details_count?: number;
    };
  };
  settlements: Settlement[];
}

export async function getSettlementSummaryRPC(
  exporter_id: number,
  season: string,
  species_id: number,
  sinister_filter: 'all' | 'sinister' | 'non_sinister' = 'all',
  include_revenues: boolean = false,
  divisionId: number | null = currentDivisionId()
): Promise<SettlementSummaryResult | null> {
  try {
    const { data, error } = await supabase.rpc('get_settlement_summary', {
      p_exporter_id: exporter_id,
      p_season: season,
      p_species_id: species_id,
      p_division_id: divisionId,
      p_sinister_filter: sinister_filter,
      p_include_revenues: include_revenues,
    });

    if (error) throw error;
    return data as unknown as SettlementSummaryResult;
  } catch (error) {
    console.error('Error getting settlement summary RPC:', error);
    return null;
  }
}

export async function getSettlementSummaryRPCV2(
  exporter_id: number,
  season: string,
  species_id: number,
  sinister_filter: 'all' | 'sinister' | 'non_sinister' = 'all',
  include_revenues: boolean = false,
  divisionId: number | null = currentDivisionId()
): Promise<SettlementSummaryResult | null> {
  try {
    const { data, error } = await supabase.rpc('get_settlement_summary_v2', {
      p_exporter_id: exporter_id,
      p_season: season,
      p_species_id: species_id,
      p_division_id: divisionId,
      p_sinister_filter: sinister_filter,
      p_include_revenues: include_revenues,
    });

    if (error) throw error;
    return data as unknown as SettlementSummaryResult;
  } catch (error) {
    console.error('Error getting settlement summary RPC v2:', error);
    return null;
  }
}

/**
 * Lightweight version of getSettlementsByExporterId for the summary page.
 * Selects only the fields actually used, drops unused joins (comparable_market,
 * origin_market, full ship_trips/*), and makes settlement_detail_revenues conditional.
 */
export async function getSettlementsSummary(
  exporter_id: number | undefined,
  season?: string,
  species_id?: number,
  sinister_filter: 'all' | 'sinister' | 'non_sinister' = 'all',
  includeRevenues: boolean = false
): Promise<Settlement[] | null> {
  try {
    const revenuesJoin = includeRevenues
      ? ',settlement_detail_revenues(*)'
      : '';

    let query = supabase
      .from('settlements')
      .select(
        `
        id,
        exporter_receiver_id,
        receiver,
        legacy_receiver,
        exchange_rate,
        local_currency,
        is_sinister,
        business_type,
        market_id,
        comparable_market_id,
        date_sold,
        total_freight_usd,
        total_revenue_local,
        total_commission_local,
        total_tax_local,
        total_other_costs_local,
        total_repacking_costs_local,
        total_other_revenue_local,
        settlement_details:settlements_details(
          id,
          kilos,
          usd_total_fob,
          variety,
          caliber,
          color,
          format,
          unit_weight,
          label,
          difference_vs_market_eta,
          local_repacking_costs,
          usd_freight
          ${revenuesJoin}
        ),
        ship_trip:ship_trips(
          id,
          eta,
          etd,
          ship:ships(type,name)
        ),
        market:markets!settlements_market_id_fkey(id,name,country),
        exporter_receiver:exporter_receivers!settlements_exporter_receiver_id_fkey(id,name)
      `
      )
      .eq('exporter_id', exporter_id)
      .eq('species_id', species_id)
      .eq('upload_status', 'approved');

    if (season) {
      query = query.eq('season', season);
    }

    if (sinister_filter === 'non_sinister') {
      query = query.eq('is_sinister', false);
    } else if (sinister_filter === 'sinister') {
      query = query.eq('is_sinister', true);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data as unknown as Settlement[];
  } catch (error) {
    console.error('Error getting settlements summary:', error);
    return null;
  }
}

export async function getSettlementNoDetailsByExporterId(
  exporter_id: number | undefined,
  status?: SettlementStatus,
  season?: string,
  species_id?: number,
  sinister_filter: 'all' | 'sinister' | 'non_sinister' = 'all',
  divisionId: number | null = null
): Promise<Settlement[] | null> {
  try {
    let query = supabase
      .from('settlements')
      .select(
        `
        *,
        ship_trip:ship_trips(
          *,
          ship:ships(*)
        )
      `
      )
      .eq('exporter_id', exporter_id)
      .eq('species_id', species_id);

    if (divisionId) query = query.eq('division_id', divisionId);

    if (status) {
      query = query.eq('upload_status', status);
    }

    if (season) {
      query = query.eq('season', season);
    }

    if (sinister_filter === 'non_sinister') {
      query = query.eq('is_sinister', false);
    } else if (sinister_filter === 'sinister') {
      query = query.eq('is_sinister', true);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error getting settlements:', error);
    return null;
  }
}

export async function getSettlementsExcludedExporter(
  excludedExporterId: number
): Promise<Settlement[] | null> {
  try {
    const { data, error } = await supabase
      .from('settlements')
      .select(
        `
        *,
        exporter:exporters(*),
        settlement_details:settlements_details(*),
        ship_trip:ship_trips(
          *,
          ship:ships(*)
        )
      `
      )
      .neq('exporter_id', excludedExporterId);

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error getting settlements:', error);
    return null;
  }
}

export async function getSettlementsVsExporters(
  exporter_id: number,
  session: { access_token: string } | null,
  sinister_filter: 'all' | 'sinister' | 'non_sinister' = 'all',
  speciesId?: number,
  season?: string,
  include_revenues?: boolean,
  // Optional sidebar-side narrowing applied only to the user's own settlements;
  // industry benchmark stays broad.
  narrow?: {
    receivers?: string[];
    markets?: string[];
    countries?: string[];
    cost_methodology?: string;
  }
): Promise<{
  rankingsClient: ClientRankingsExporters[];
  rankingsVariety: ClientRankingsExporters[];
  rankingsCaliber: ClientRankingsExporters[];
} | null> {
  try {
    if (!session?.access_token) {
      throw new Error('No authentication token available');
    }

    const { data } = await axios.post(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-vs-exporters`,
      {
        exporter_id,
        division_id: currentDivisionId(),
        sinister_filter,
        speciesId,
        season,
        include_revenues,
        receivers: narrow?.receivers,
        markets: narrow?.markets,
        countries: narrow?.countries,
        cost_methodology: narrow?.cost_methodology,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
      }
    );
    console.log('rankings data', data);
    return data;
  } catch (error) {
    console.error('Error getting rankings:', error);
    return null;
  }
}

export async function getSettlementsFobVsExporters(
  session: { access_token: string } | null,
  methodology: 'by_volume' | 'by_exporter' = 'by_volume',
  season?: string,
  species_id?: number,
  sinister_filter: 'all' | 'sinister' | 'non_sinister' = 'all',
  separation: 'none' | 'by_label' | 'by_receiver' = 'none',
  weekNumber?: number,
  variety?: string,
  dateMethodology?: 'date_sold' | 'eta' | 'etd',
  incoterm?: 'fob' | 'cif',
  countries?: string[],
  market_ids?: string[],
  ship_trip_ids?: string[],
  label_categories?: string[],
  aggregation?: 'global',
  include_revenues: boolean = false
): Promise<ClientRankingsExporters[] | null> {
  try {
    if (!session?.access_token) {
      throw new Error('No authentication token available');
    }

    let url = `${
      import.meta.env.VITE_SUPABASE_URL
    }/functions/v1/get-fob-vs-exporters?methodology=${methodology}&season=${season}&species_id=${species_id}&sinister_filter=${sinister_filter}${currentDivisionId() ? `&division_id=${currentDivisionId()}` : ''}&separation=${separation}${
      weekNumber ? `&weekNumber=${weekNumber}` : ''
    }${variety ? `&variety=${variety}` : ''}${
      dateMethodology ? `&dateField=${dateMethodology}` : ''
    }${incoterm ? `&incoterm=${incoterm}` : ''}${
      countries && countries.length > 0
        ? `&countries=${countries.join(',')}`
        : ''
    }${ship_trip_ids && ship_trip_ids.length > 0 ? `&ship_trip_ids=${ship_trip_ids.join(',')}` : ''}${market_ids && market_ids.length > 0 ? `&market_ids=${market_ids.join(',')}` : ''}${label_categories && label_categories.length > 0 ? `&label_categories=${label_categories.join(',')}` : ''}${aggregation ? `&aggregation=${aggregation}` : ''}&include_revenues=${include_revenues}`;

    console.log('API URL:', url);

    const { data: data } = await axios.get(url, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
    });
    return data;
  } catch (error) {
    console.error('Error getting rankings:', error);
    return null;
  }
}

export async function checkLabelCompliance(
  session: { access_token: string } | null,
  species_id?: number,
  season?: string
): Promise<{
  hasLabels: boolean;
  isCompliant: boolean;
  nonCompliantLabels: string[];
  categories: string[];
} | null> {
  try {
    if (!session?.access_token) {
      throw new Error('No authentication token available');
    }

    const url = `${
      import.meta.env.VITE_SUPABASE_URL
    }/functions/v1/check-label-compliance?species_id=${species_id}&season=${season}${currentDivisionId() ? `&division_id=${currentDivisionId()}` : ''}`;

    const { data } = await axios.get(url, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
    });
    return data;
  } catch (error) {
    console.error('Error checking label compliance:', error);
    return null;
  }
}

export const getSettlementsSalesVsExporters = async (
  session: Session,
  exporterId: number,
  methodology: 'by_volume' | 'by_exporter',
  season: string,
  separation: string = 'none',
  variety?: string,
  dateField: string = 'date_sold',
  includeColor: boolean = false,
  speciesId?: number,
  startDate?: string,
  endDate?: string,
  groupBy: string = 'week',
  market_ids?: string[]
): Promise<any> => {
  // Use the new consolidated edge function
  let url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-sales-vs-exporters?exporter_id=${exporterId}&season=${season}&separation=${separation}&methodology=${methodology}&dateField=${dateField}&groupBy=${groupBy}`;

  if (variety) {
    url += `&variety=${encodeURIComponent(variety)}`;
  }

  if (includeColor) {
    url += `&includeColor=true`;
  }

  if (speciesId) {
    url += `&species_id=${speciesId}`;
  }
  if (currentDivisionId()) {
    url += `&division_id=${currentDivisionId()}`;
  }

  if (startDate) {
    url += `&startDate=${startDate}`;
  }

  if (endDate) {
    url += `&endDate=${endDate}`;
  }

  if (market_ids && market_ids.length > 0) {
    url += `&market_ids=${market_ids.join(',')}`;
  }

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${session?.access_token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Error ${response.status}: ${errorText}`);
  }

  return response.json();
};

export const getSettlementsEvolutionData = async (
  session: Session,
  exporterId: number,
  methodology: 'by_volume' | 'by_exporter',
  season: string,
  evolutionGroupBy: string,
  variety?: string,
  dateField: string = 'date_sold',
  speciesId?: number,
  startDate?: string,
  endDate?: string,
  groupBy: string = 'week',
  market_ids?: string[],
  separation: string = 'none'
): Promise<any> => {
  // Use the same consolidated edge function but with evolution_group_by parameter
  let url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-sales-vs-exporters?exporter_id=${exporterId}&season=${season}&separation=${separation}&methodology=${methodology}&dateField=${dateField}&groupBy=${groupBy}&evolution_group_by=${evolutionGroupBy}`;

  if (variety) {
    url += `&variety=${encodeURIComponent(variety)}`;
  }

  if (speciesId) {
    url += `&species_id=${speciesId}`;
  }
  if (currentDivisionId()) {
    url += `&division_id=${currentDivisionId()}`;
  }

  if (startDate) {
    url += `&startDate=${startDate}`;
  }

  if (endDate) {
    url += `&endDate=${endDate}`;
  }

  if (market_ids && market_ids.length > 0) {
    url += `&market_ids=${market_ids.join(',')}`;
  }

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${session?.access_token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Error ${response.status}: ${errorText}`);
  }

  return response.json();
};

// New optimized functions

export const getAvailableVarieties = async (
  session: Session,
  season: string,
  speciesId: number,
  startDate?: string,
  endDate?: string,
  dateField: string = 'date_sold',
  market_ids?: string[]
): Promise<{ variety: string; total_kilos: number }[]> => {
  let url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-available-varieties?season=${season}&species_id=${speciesId}&dateField=${dateField}`;

  if (startDate) {
    url += `&startDate=${startDate}`;
  }

  if (endDate) {
    url += `&endDate=${endDate}`;
  }

  if (market_ids && market_ids.length > 0) {
    url += `&market_ids=${market_ids.join(',')}`;
  }

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${session?.access_token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Error ${response.status}: ${errorText}`);
  }

  return response.json();
};

export const getSettlementsSalesVsExportersDispersion = async (
  session: Session,
  exporterId: number,
  methodology: 'by_volume' | 'by_exporter',
  season: string,
  variety: string, // Required for dispersion
  separation: string = 'none',
  dateField: string = 'date_sold',
  speciesId?: number,
  startDate?: string,
  endDate?: string,
  groupBy: string = 'week',
  market_ids?: string[]
): Promise<any> => {
  let url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-sales-vs-exporters-dispersion?exporter_id=${exporterId}&season=${season}&variety=${encodeURIComponent(variety)}&separation=${separation}&methodology=${methodology}&dateField=${dateField}&groupBy=${groupBy}`;

  if (speciesId) {
    url += `&species_id=${speciesId}`;
  }
  if (currentDivisionId()) {
    url += `&division_id=${currentDivisionId()}`;
  }

  if (startDate) {
    url += `&startDate=${startDate}`;
  }

  if (endDate) {
    url += `&endDate=${endDate}`;
  }

  if (market_ids && market_ids.length > 0) {
    url += `&market_ids=${market_ids.join(',')}`;
  }

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${session?.access_token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Error ${response.status}: ${errorText}`);
  }

  return response.json();
};

export async function getSettlementByExporterIdAndRef(
  exporter_id: number,
  reference: string
): Promise<Settlement | null> {
  try {
    const { data, error } = await supabase
      .from('settlements')
      .select('*')
      .eq('exporter_id', exporter_id)
      .eq('reference_number', reference)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error getting settlement:', error);
    return null;
  }
}

export async function createSettlementDetails(
  details: Omit<SettlementDetails, 'id' | 'settlement'>
): Promise<SettlementDetails[] | null> {
  try {
    const { data, error } = await supabase
      .from('settlements_details')
      .insert([details]) // Wrap details in an array
      .select();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating settlement details:', error);
    return null;
  }
}

export async function createSettlementDetail(
  detail: Omit<SettlementDetails, 'id' | 'settlement'>
): Promise<SettlementDetails | null> {
  try {
    const { data, error } = await supabase
      .from('settlements_details')
      .insert([detail])
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating settlement detail:', error);
    return null;
  }
}

export async function getSettlementById(
  id: number
): Promise<Settlement | null> {
  try {
    const { data, error } = await supabase
      .from('settlements')
      .select(
        `
        *,
        settlement_details:settlements_details(
          *,
          settlement_detail_revenues(*)
        ),
        ship_trip:ship_trips(
          *,
          ship:ships(*),
          market:markets!ship_trips_market_id_fkey(*),
          origin_market:markets!ship_trips_origin_market_id_fkey(*)
        ),
        market:markets!settlements_market_id_fkey(*),
        comparable_market:markets!settlements_comparable_market_id_fkey(*),
        exporter_receiver:exporter_receivers!settlements_exporter_receiver_id_fkey(*)
      `
      )
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error getting settlement:', error);
    return null;
  }
}

interface GetSettlementsParams {
  start?: number;
  limit?: number;
  searchTerm?: string;
  season?: string;
  showPendingOnly?: boolean;
}

interface GetSettlementsBySuperAdminParams {
  start?: number;
  limit?: number;
  searchTerm?: string;
  season?: string;
  status?: SettlementStatus | 'all';
  includeDetails?: boolean;
  detailsAttributes?: string[];
  exporterId?: number;
}

export async function getSettlementsBySuperAdmin(
  params?: GetSettlementsBySuperAdminParams
): Promise<{ data: Settlement[]; count: number }> {
  try {
    // Define what to select based on whether details are needed
    let selectQuery = `
      *,
      exporter:exporters(*),
      species:species(*),
      ship_trip:ship_trips(
        *,
        ship:ships(*),
        market:markets!ship_trips_market_id_fkey(*),
        origin_market:markets!ship_trips_origin_market_id_fkey(*)
      ),
      market:markets!settlements_market_id_fkey(*)
    `;

    // Add settlement details if requested
    if (params?.includeDetails) {
      const detailsAttrs = params.detailsAttributes?.length
        ? params.detailsAttributes.join(',')
        : '*';

      selectQuery += `,
        settlement_details:settlements_details(${detailsAttrs})
      `;
    }

    // Build the base query
    let baseQuery = supabase
      .from('settlements')
      .select(selectQuery, { count: 'exact' });

    // Apply filters
    if (params?.searchTerm) {
      baseQuery = baseQuery.or(
        `reference_number.ilike.%${params.searchTerm}%,receiver.ilike.%${params.searchTerm}%,container_number.ilike.%${params.searchTerm}%`
      );
    }

    if (params?.season) {
      baseQuery = baseQuery.eq('season', params.season);
    }

    if (params?.status && params.status !== 'all') {
      baseQuery = baseQuery.eq('upload_status', params.status);
    }

    if (params?.exporterId) {
      baseQuery = baseQuery.eq('exporter_id', params.exporterId);
    }

    // Apply ordering
    let query = baseQuery.order('id', { ascending: false });

    // Apply pagination if specified
    if (params?.limit !== undefined && params?.start !== undefined) {
      query = query.range(params.start, params.start + params.limit - 1);
    }

    const { data, error, count } = await query;

    if (error) {
      console.error('Supabase error getting settlements:', error);
      return { data: [], count: 0 };
    }

    // Ensure we return a proper Settlement array
    return {
      data: (Array.isArray(data) ? data : []) as unknown as Settlement[],
      count: count || 0,
    };
  } catch (error) {
    console.error('Error getting settlements for superadmin:', error);
    return {
      data: [],
      count: 0,
    };
  }
}

export async function getSettlements(params?: GetSettlementsParams) {
  try {
    let baseQuery = supabase.from('settlements').select(
      `
        *,
        settlement_details:settlements_details(*),
        ship_trip:ship_trips(
          *,
          ship:ships(*)
        ),
        exporter:exporters(*)
      `,
      { count: 'exact' }
    );

    // Aplicar filtros si existen
    if (params?.searchTerm) {
      baseQuery = baseQuery.or(
        `reference_number.ilike.%${params.searchTerm}%,receiver.ilike.%${params.searchTerm}%,container_number.ilike.%${params.searchTerm}%`
      );
    }

    if (params?.season) {
      baseQuery = baseQuery.eq('season', params.season);
    }

    if (params?.showPendingOnly) {
      baseQuery = baseQuery.eq('upload_status', 'pending');
    }

    // Aplicar orden y paginación
    let query = baseQuery.order('id', { ascending: false });

    if (params?.limit !== undefined && params?.start !== undefined) {
      query = query.range(params.start, params.start + params.limit - 1);
    }

    const { data, error, count } = await query;

    if (error) throw error;

    return {
      data: data || [],
      count: count || 0,
    };
  } catch (error) {
    console.error('Error getting settlements:', error);
    return {
      data: [],
      count: 0,
    };
  }
}

export async function updateSettlement(
  id: number,
  data: Partial<Settlement> & {
    settlement_details?: Array<{
      id: number;
      usd_freight: number;
      usd_total_fob: number;
      local_currency: string;
      pallet?: string;
      variety?: string;
      format?: string;
      caliber?: string;
      color?: string;
      label?: string;
    }>;
  },
  withEmail: boolean = false,
  notifyExporter?: boolean
): Promise<Settlement | null> {
  try {
    const { settlement_details, ...settlementData } = data;

    // console.log('settlement_details', settlement_details);
    // console.log('settlementData', settlementData);

    // First update the settlement
    const { data: updatedSettlement, error: settlementError } = await supabase
      .from('settlements')
      .update(settlementData)
      .eq('id', id)
      .select()
      .single();

    if (settlementError) throw settlementError;

    // Then update all settlement details in parallel
    if (settlement_details && settlement_details.length > 0) {
      const updatePromises = settlement_details.map((detail) => {
        const updateData: any = {
          usd_freight: detail.usd_freight,
          usd_total_fob: detail.usd_total_fob,
          local_currency: detail.local_currency,
        };

        // Add optional fields if they exist
        if (detail.pallet !== undefined) updateData.pallet = detail.pallet;
        if (detail.variety !== undefined) updateData.variety = detail.variety;
        if (detail.format !== undefined) updateData.format = detail.format;
        if (detail.caliber !== undefined) updateData.caliber = detail.caliber;
        if (detail.color !== undefined) updateData.color = detail.color;
        if (detail.label !== undefined) updateData.label = detail.label;

        return supabase
          .from('settlements_details')
          .update(updateData)
          .eq('id', detail.id);
      });

      const results = await Promise.all(updatePromises);
      const errors = results.filter((result) => result.error);
      if (errors.length > 0) throw errors[0].error;
    }

    // Fetch the updated settlement with all its details
    const { data: finalSettlement, error: fetchError } = await supabase
      .from('settlements')
      .select(
        `
        *,
        settlement_details:settlements_details(*),
        ship_trip:ship_trips(
          *,
          ship:ships(*)
        ),
        exporter:exporters(*),
        exporter_receiver:exporter_receivers!settlements_exporter_receiver_id_fkey(*)
      `
      )
      .eq('id', id)
      .single();

    if (fetchError) throw fetchError;
    console.log('notifyExporter', notifyExporter);
    if (settlementData.upload_status === 'approved') {
      try {
        if (withEmail && notifyExporter) {
          console.log('sending email');

          await sendEmail({
            exporter_id: updatedSettlement.exporter_id,
            subject: `Liquidación Subida - ${updatedSettlement.receiver} - ${updatedSettlement.reference_number}`,
            html: `
            <p>Su liquidación ha sido subida con los siguientes detalles:</p>
            <ul>
              <li>Referencia: ${updatedSettlement.reference_number}</li>
              <li>Contenedor: ${updatedSettlement.container_number}</li>
            </ul>
          `,
            link: `https://portal.cherrymarket.cl/settlements/details`,
            linkText: 'Revisa tu liquidación en CherryMarket',
          });
        }
      } catch (emailError) {
        console.error('Error sending approval email:', emailError);
        // Don't throw error here - we don't want to fail the update if email fail
      }
    }

    return finalSettlement;
  } catch (error) {
    console.error('Error updating settlement:', error);
    return null;
  }
}

export async function setSettlementReceiverForLegacyName(params: {
  exporterId: number;
  legacyReceiverName: string;
  exporterReceiverId: number;
}): Promise<{ updated_settlements: number } | null> {
  try {
    const { data, error } = await supabase.rpc(
      'set_settlement_receiver_for_legacy_name',
      {
        p_exporter_id: params.exporterId,
        p_legacy_receiver_name: params.legacyReceiverName,
        p_exporter_receiver_id: params.exporterReceiverId,
      }
    );

    if (error) throw error;
    return data as { updated_settlements: number };
  } catch (error) {
    console.error('Error setting settlement receiver for legacy name:', error);
    return null;
  }
}

export async function updateSettlementShipTripByShipTripId(
  old_ship_trip_id: number,
  new_ship_trip_id: number
): Promise<Settlement[] | null> {
  try {
    const { data, error } = await supabase
      .from('settlements')
      .update({ ship_trip_id: new_ship_trip_id })
      .eq('ship_trip_id', old_ship_trip_id)
      .select();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating settlement:', error);
    return null;
  }
}

export const getClaimsBlockingSettlementDeletion = async (
  settlementId: number
): Promise<{ id: number }[]> => {
  try {
    const { data: details } = await supabase
      .from('settlements_details')
      .select('id')
      .eq('settlement_id', settlementId);

    if (!details || details.length === 0) return [];

    const detailIds = details.map((d) => d.id);

    const { data: claimDetails } = await supabase
      .from('claims_details')
      .select('claim_id')
      .in('settlement_detail_id', detailIds);

    if (!claimDetails || claimDetails.length === 0) return [];

    const uniqueClaimIds = [...new Set(claimDetails.map((cd) => cd.claim_id))];

    return uniqueClaimIds.map((id) => ({ id }));
  } catch (error) {
    console.error('Error checking claims for settlement:', error);
    return [];
  }
};

export const deleteSettlement = async (id: number): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('settlements')
      .delete()
      .eq('id', id)
      .eq('is_blocked', false);
    return error ? false : true;
  } catch (error) {
    console.error('Error deleting settlement:', error);
    return false;
  }
};

export const deleteSettlementAudited = async (
  settlementId: number,
  accessToken: string,
  reason?: string
): Promise<boolean> => {
  try {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-settlement`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ settlement_id: settlementId, reason }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Error deleting settlement (audited):', errorData);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error deleting settlement (audited):', error);
    return false;
  }
};

export async function getUniqueVarietiesWithCount(
  session: { access_token: string } | null,
  season: string,
  species_id: number
) {
  try {
    if (!session?.access_token) {
      throw new Error('No authentication token available');
    }

    const { data, error } = await supabase.rpc(
      'get_unique_varieties_with_count',
      {
        p_season: season,
        p_species_id: species_id,
      }
    );

    if (error) throw error;

    return (data || []) as Array<{ variety: string; count: number }>;
  } catch (error) {
    console.error('Error getting unique varieties:', error);
    return [];
  }
}

export async function getUniqueCalibersWithCount(
  session: { access_token: string } | null,
  season: string,
  species_id: number,
  exporter_id: number,
  divisionId: number | null = currentDivisionId()
) {
  try {
    if (!session?.access_token) {
      throw new Error('No authentication token available');
    }

    const { data, error } = await supabase.rpc(
      'get_exporter_unique_calibers_with_count',
      {
        p_exporter_id: exporter_id,
        p_season: season,
        p_species_id: species_id,
        p_division_id: divisionId,
      }
    );

    if (error) throw error;

    return (data || []) as Array<{ caliber: string; count: number }>;
  } catch (error) {
    console.error('Error getting unique calibers:', error);
    return [];
  }
}

export async function updateSettlementDetailField(
  detailId: number,
  field: string,
  value: string
): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('settlements_details')
      .update({ [field]: value })
      .eq('id', detailId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error updating settlement detail field:', error);
    return false;
  }
}

export async function updateSettlementDetailFieldAndFetch(
  settlementId: number,
  detailId: number,
  field: string,
  value: string
): Promise<Settlement | null> {
  try {
    // First update the detail field
    const { error } = await supabase
      .from('settlements_details')
      .update({ [field]: value })
      .eq('id', detailId);

    if (error) throw error;

    // Then fetch the updated settlement
    const { data: settlement, error: fetchError } = await supabase
      .from('settlements')
      .select(
        `
        *,
        settlement_details:settlements_details(*),
        ship_trip:ship_trips(
          *,
          ship:ships(*)
        ),
        exporter:exporters(*)
      `
      )
      .eq('id', settlementId)
      .single();

    if (fetchError) throw fetchError;
    return settlement;
  } catch (error) {
    console.error('Error updating settlement detail field:', error);
    return null;
  }
}

export async function updateSettlementDetail(
  settlementDetail: SettlementDetails
): Promise<Settlement | null> {
  try {
    // Sanitize potential UI-derived format decorations like " / 5KG"
    const { settlement_detail_revenues, ...payload }: any = {
      ...settlementDetail,
    };
    if (typeof payload.format === 'string' && payload.format.includes(' / ')) {
      // Keep only the base format label (e.g., 'CAJA', '2IN1')
      payload.format = payload.format.split(' / ')[0];
    }
    // First update the detail field
    const { error } = await supabase
      .from('settlements_details')
      .update(payload)
      .eq('id', settlementDetail.id);

    if (error) throw error;

    // Then fetch the updated settlement
    const { data: settlement, error: fetchError } = await supabase
      .from('settlements')
      .select(
        `
        *,
        settlement_details:settlements_details(*),
        ship_trip:ship_trips(
          *,
          ship:ships(*)
        ),
        exporter:exporters(*)
      `
      )
      .eq('id', settlementDetail.settlement_id)
      .single();

    if (fetchError) throw fetchError;
    return settlement;
  } catch (error) {
    console.error('Error updating settlement detail field:', error);
    return null;
  }
}

export async function deleteSettlementDetail(
  detailId: number
): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('settlements_details')
      .delete()
      .eq('id', detailId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting settlement detail:', error);
    return false;
  }
}

export interface SettlementExistsResult {
  exists: boolean;
  isBlocked?: boolean;
  settlementId?: number;
  isApproved?: boolean;
}

export async function checkSettlementExists(
  referenceNumber: string,
  season: string,
  speciesId: number,
  exporterId: number
): Promise<SettlementExistsResult> {
  try {
    const trimmedRef = referenceNumber.trim();
    // maybeSingle: "no existe" es un resultado normal (null), no un error 406.
    const { data: existingSettlement, error } = await supabase
      .from('settlements')
      .select('id, is_blocked, upload_status')
      .eq('exporter_id', exporterId)
      .eq('reference_number', trimmedRef)
      .eq('season', season)
      .eq('species_id', speciesId)
      .maybeSingle();

    if (error) throw error;

    if (existingSettlement) {
      return {
        exists: true,
        isBlocked: existingSettlement.is_blocked,
        settlementId: existingSettlement.id,
        isApproved: existingSettlement.upload_status === 'approved',
      };
    }

    return { exists: false };
  } catch (error) {
    console.error('Error checking existing settlement:', error);
    return { exists: false };
  }
}

// Evolution Chart Functions with optimized filtering

export const getSettlementsEvolutionDataOptimized = async (
  session: Session,
  exporterId: number,
  methodology: 'by_volume' | 'by_exporter',
  season: string,
  evolutionGroupBy: 'caliber' | 'variety' | 'format' | 'market' | 'color',
  dateField: string = 'date_sold',
  speciesId?: number,
  startDate?: string,
  endDate?: string,
  groupBy: string = 'week',
  market_ids?: string[],
  separation: string = 'none',
  // Specific filters to avoid timeout
  filterVarieties?: string[],
  filterCaliber?: string,
  filterFormats?: string[],
  filterColors?: string[]
): Promise<any> => {
  let url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-sales-vs-exporters-evolution?exporter_id=${exporterId}&season=${season}&separation=${separation}&methodology=${methodology}&dateField=${dateField}&groupBy=${groupBy}&evolution_group_by=${evolutionGroupBy}`;

  if (speciesId) {
    url += `&species_id=${speciesId}`;
  }
  if (currentDivisionId()) {
    url += `&division_id=${currentDivisionId()}`;
  }

  if (startDate) {
    url += `&startDate=${startDate}`;
  }

  if (endDate) {
    url += `&endDate=${endDate}`;
  }

  if (market_ids && market_ids.length > 0) {
    url += `&market_ids=${market_ids.join(',')}`;
  }

  // Add specific filters
  if (filterVarieties && filterVarieties.length > 0) {
    url += `&filter_variety=${encodeURIComponent(filterVarieties.join(','))}`;
  }

  if (filterCaliber) {
    url += `&filter_caliber=${encodeURIComponent(filterCaliber)}`;
  }

  if (filterFormats && filterFormats.length > 0) {
    url += `&filter_format=${encodeURIComponent(filterFormats.join(','))}`;
  }

  if (filterColors && filterColors.length > 0) {
    url += `&filter_color=${encodeURIComponent(filterColors.join(','))}`;
  }

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${session?.access_token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Error ${response.status}: ${errorText}`);
  }

  return response.json();
};

export interface EvolutionFilterData {
  value: string;
  total_kilos: number;
}

export interface EvolutionFilters {
  varieties: EvolutionFilterData[];
  calibers: EvolutionFilterData[];
  formats: EvolutionFilterData[];
  colors: EvolutionFilterData[];
}

export const getAvailableEvolutionFilters = async (
  session: Session,
  season: string,
  speciesId: number,
  startDate?: string,
  endDate?: string,
  dateField: string = 'date_sold',
  market_ids?: string[]
): Promise<EvolutionFilters> => {
  let url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-available-evolution-filters?season=${season}&species_id=${speciesId}&dateField=${dateField}`;

  if (startDate) {
    url += `&startDate=${startDate}`;
  }

  if (endDate) {
    url += `&endDate=${endDate}`;
  }

  if (market_ids && market_ids.length > 0) {
    url += `&market_ids=${market_ids.join(',')}`;
  }

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${session?.access_token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Error ${response.status}: ${errorText}`);
  }

  return response.json();
};

export const getSentMarketsByExporter = async (
  session: Session,
  season: string,
  speciesId: number
): Promise<
  { id: string; name: string; country: string; created_at?: string }[]
> => {
  try {
    if (!session?.access_token) {
      throw new Error('No authentication token available');
    }

    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-sent-markets-by-exporter?season=${season}&species_id=${speciesId}${currentDivisionId() ? `&division_id=${currentDivisionId()}` : ''}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Error ${response.status}: ${errorText}`);
    }

    return response.json();
  } catch (error) {
    console.error('Error getting sent markets by exporter:', error);
    return [];
  }
};

export const getSentShipTripsByExporter = async (
  session: Session,
  season: string,
  speciesId: number
): Promise<
  {
    id: number;
    eta: string;
    etd: string;
    ship_name: string;
    market_id: string;
    market_name: string;
    market_country: string;
  }[]
> => {
  try {
    if (!session?.access_token) {
      throw new Error('No authentication token available');
    }

    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-sent-ship-trips?season=${season}&species_id=${speciesId}${currentDivisionId() ? `&division_id=${currentDivisionId()}` : ''}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Error ${response.status}: ${errorText}`);
    }

    return response.json();
  } catch (error) {
    console.error('Error getting sent ship trips by exporter:', error);
    return [];
  }
};
